import { makeAutoObservable, runInAction } from "mobx";
import { i18nStore } from "../i18n/i18nStore.js";
import { isSupportedLanguage } from "@aid/shared";

export type RegistrationMode = "open" | "invite_only" | "closed";

export type AuthenticatedUser = {
  id: string;
  email: string;
  tokenBalance: number;
  language?: string;
  tokenPurchasesEnabled: boolean;
};

type AuthenticatedUserPayload = Omit<AuthenticatedUser, "tokenPurchasesEnabled"> & {
  tokenPurchasesEnabled?: unknown;
};

function normalizeAuthenticatedUser(user: AuthenticatedUserPayload): AuthenticatedUser {
  return { ...user, tokenPurchasesEnabled: user.tokenPurchasesEnabled === true };
}

export class AuthRequestError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

function readStoredToken(): string | null {
  return typeof localStorage === "undefined" ? null : localStorage.getItem("rpg_token");
}

function storeToken(token: string): void {
  if (typeof localStorage !== "undefined") localStorage.setItem("rpg_token", token);
}

function removeStoredToken(): void {
  if (typeof localStorage !== "undefined") localStorage.removeItem("rpg_token");
}

export class AuthStore {
  user: AuthenticatedUser | null = null;
  token: string | null = readStoredToken();
  registrationMode: RegistrationMode = "closed";
  registrationPolicyLoaded = false;
  invitationToken: string | null = null;
  invitedEmail: string | null = null;
  invitationErrorCode: string | null = null;

  constructor() {
    makeAutoObservable(this);
    if (this.token) {
      this.fetchUserFromToken();
    }
  }

  get isAuthenticated() {
    return !!this.user;
  }

  get canRegister() {
    return this.registrationMode === "open" ||
      (this.registrationMode === "invite_only" && !!this.invitedEmail);
  }

  async loadRegistrationPolicy() {
    try {
      const res = await fetch("/api/auth/registration-policy");
      if (!res.ok) throw new Error("Policy request failed");
      const data = await res.json() as { mode: RegistrationMode };
      if (!["open", "invite_only", "closed"].includes(data.mode)) {
        throw new Error("Invalid registration policy");
      }
      runInAction(() => {
        this.registrationMode = data.mode;
        this.registrationPolicyLoaded = true;
      });
    } catch {
      runInAction(() => {
        this.registrationMode = "closed";
        this.registrationPolicyLoaded = true;
      });
    }
  }

  async inspectInvitation(invitationToken: string) {
    this.invitationToken = invitationToken;
    this.invitedEmail = null;
    this.invitationErrorCode = null;
    try {
      const res = await fetch("/api/auth/invitation/inspect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invitationToken })
      });
      const data = await res.json();
      runInAction(() => {
        if (this.invitationToken !== invitationToken) return;
        if (res.ok) this.invitedEmail = data.email;
        else this.invitationErrorCode = data.code || "invitation_invalid";
      });
    } catch {
      runInAction(() => {
        if (this.invitationToken === invitationToken) {
          this.invitationErrorCode = "invitation_invalid";
        }
      });
    }
  }

  clearInvitation(): void {
    this.invitationToken = null;
    this.invitedEmail = null;
    this.invitationErrorCode = null;
  }

  async fetchUserFromToken() {
    if (!this.token) return;
    try {
      const payloadBase64 = this.token.split(".")[1];
      const payload = JSON.parse(atob(payloadBase64));
      runInAction(() => {
        this.user = {
          id: payload.userId || "temp-user-id",
          email: payload.email || "user@example.com",
          tokenBalance: payload.tokenBalance ?? 1000,
          tokenPurchasesEnabled: false,
        };
      });

      const res = await fetch("/api/auth/me", {
        headers: {
          "Authorization": `Bearer ${this.token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        runInAction(() => {
          this.user = normalizeAuthenticatedUser(data);
        });
        if (isSupportedLanguage(data.language)) {
          i18nStore.setLanguage(data.language);
        }
      } else {
        this.logout();
      }
    } catch (e) {
      console.warn("Failed to parse local JWT token or fetch profile:", e);
      this.logout();
    }
  }

  async login(email: string, password: string): Promise<void> {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || "Login failed");
    }

    runInAction(() => {
      this.token = data.token;
      this.user = normalizeAuthenticatedUser(data.user);
      storeToken(data.token);
    });
    if (isSupportedLanguage(data.user?.language)) {
      i18nStore.setLanguage(data.user.language);
    }
  }

  async register(email: string, password: string): Promise<void> {
    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        invitationToken: this.invitationToken,
        language: i18nStore.language
      })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new AuthRequestError(data.error || "Registration failed", data.code);
    }

    runInAction(() => {
      this.token = data.token;
      this.user = normalizeAuthenticatedUser(data.user);
      storeToken(data.token);
    });
    if (isSupportedLanguage(data.user?.language)) {
      i18nStore.setLanguage(data.user.language);
    }
  }

  async loginWithOAuth(provider: string): Promise<void> {
    const res = await fetch("/api/auth/oauth/callback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        provider,
        oauthToken: `mock-token-${provider}`,
        invitationToken: this.invitationToken,
        language: i18nStore.language
      })
    });
    const data = await res.json();
    if (!res.ok) throw new AuthRequestError(data.error || "OAuth login failed", data.code);
    this.loginWithOAuthData(data.token, data.user);
  }

  loginWithOAuthData(token: string, user: AuthenticatedUserPayload) {
    runInAction(() => {
      this.token = token;
      this.user = normalizeAuthenticatedUser(user);
      storeToken(token);
    });
    if (isSupportedLanguage(user.language)) {
      i18nStore.setLanguage(user.language);
    }
  }

  logout() {
    this.token = null;
    this.user = null;
    removeStoredToken();
  }

  async buyTokens(amount: number) {
    if (!this.token || !this.user?.tokenPurchasesEnabled) return;
    const res = await fetch("/api/billing/buy-tokens", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.token}`
      },
      body: JSON.stringify({ amount })
    });

    const data = await res.json();
    if (res.ok && this.user) {
      runInAction(() => {
        this.user!.tokenBalance = data.user.tokenBalance;
      });
    }
  }

  updateTokenBalance(balance: number) {
    if (this.user) {
      this.user.tokenBalance = balance;
    }
  }
}

export const authStore = new AuthStore();
export default authStore;
